import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  DatePicker,
  Modal,
  Select,
  Badge,
  Tooltip,
  QRCode,
  Spin
} from 'antd';
import {
  SearchOutlined,
  CheckOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { withdrawalService, paymentService } from '../../services';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const WithdrawalList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    total_records: 0,
    pending_count: 0,
    approved_count: 0,
    pending_amount: 0,
    approved_amount: 0
  });
  
  // Filters
  const [dateRange, setDateRange] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  

  
  // Modal states
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // QR Payment Modal states
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);



  // Load data on component mount
  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      const params = {
        accessToken: token,
        ...(dateRange.length === 2 && {
          dateFrom: dateRange[0].format('YYYY-MM-DD'),
          dateTo: dateRange[1].format('YYYY-MM-DD')
        }),
        ...(statusFilter && {
          trangThai: statusFilter
        })
      };
      
      const response = await withdrawalService.getWithdrawalHistory(params);
      
      if (response.success) {
        let filteredData = response.data.withdrawal_history || [];
        
        // Lọc theo trạng thái phía client nếu server chưa hỗ trợ
        if (statusFilter && params.trangThai) {
          filteredData = filteredData.filter(item => item.withdrawal?.status === statusFilter);
        }
        
        setData(filteredData);
        setSummary(response.data.summary || {});
        message.success(`${response.message} (${filteredData.length} bản ghi)`);
      } else {
        message.error(response.message || 'Lỗi không xác định');
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      message.error('Lỗi kết nối API');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWithdrawalsSimulate = () => {
    // Chức năng duyệt tạm thời khi API chưa sẵn sàng
    const pendingItems = data.filter(item => item.withdrawal?.status === 'Đợi Duyệt');
    
    Modal.confirm({
      title: 'Mô phỏng duyệt rút tiền',
      content: (
        <div>
          <p>Chức năng này chỉ mô phỏng vì API chưa được implement.</p>
          <p>Sẽ "duyệt" <strong>{pendingItems.length}</strong> yêu cầu rút tiền.</p>
          {approvalNote && (
            <p>Ghi chú: <em>{approvalNote}</em></p>
          )}
        </div>
      ),
      okText: 'Mô phỏng duyệt',
      cancelText: 'Hủy',
      onOk: () => {
        // Simulate approval
        const updatedData = data.map(item => {
          if (item.withdrawal?.status === 'Đợi Duyệt') {
            return {
              ...item,
              withdrawal: {
                ...item.withdrawal,
                status: 'Đã Duyệt',
                note: item.withdrawal.note + (approvalNote ? ` | ${approvalNote}` : ' | Đã duyệt (mô phỏng)')
              },
              approved_info: {
                approved_at: new Date().toISOString(),
                approved_by: 'Admin (Mô phỏng)'
              }
            };
          }
          return item;
        });
        
        setData(updatedData);
        
        // Update summary
        const newSummary = {
          ...summary,
          pending_count: 0,
          approved_count: summary.total_records,
          pending_amount: 0,
          approved_amount: summary.pending_amount + summary.approved_amount
        };
        setSummary(newSummary);
        
        message.success(`Đã mô phỏng duyệt ${pendingItems.length} yêu cầu rút tiền`);
        
        setApprovalModalVisible(false);
        setApprovalNote('');
      }
    });
  };

  const handleApproveWithdrawals = async () => {
    setApprovalLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const params = {
        accessToken: token,
        ...(dateRange.length === 2 && {
          dateFrom: dateRange[0].format('YYYY-MM-DD'),
          dateTo: dateRange[1].format('YYYY-MM-DD')
        }),
        ...(approvalNote && {
          ghiChu: approvalNote
        })
      };
      
      const response = await withdrawalService.approveWithdrawals(params);
      
      if (response.success) {
        message.success(response.message);
        setApprovalModalVisible(false);
        setApprovalNote('');
        fetchWithdrawals(); // Reload data
      } else {
        message.error(response.message || 'Lỗi không xác định');
      }
    } catch (error) {
      console.error('Error approving withdrawals:', error);
      
      // Kiểm tra nếu là lỗi 404 hoặc 500 thì có thể API chưa implement
      if (error.response?.status === 404) {
        message.warning('API duyệt rút tiền chưa được implement trên server');
      } else if (error.response?.status === 500) {
        message.error('Lỗi server khi xử lý duyệt rút tiền');
      } else {
        message.error(`Lỗi kết nối API: ${error.message}`);
      }
    } finally {
      setApprovalLoading(false);
    }
  };

  // Hiển thị QR thanh toán
  const handleShowQRPayment = async (record) => {
    setSelectedRecord(record);
    setQrModalVisible(true);
    setQrLoading(true);
    setQrData(null);

    try {
      const response = await paymentService.generateCtvQR(record.id);
      
      if (response.success) {
        setQrData(response.data);
        message.success('Tạo QR thanh toán thành công');
      } else {
        message.error(response.message || 'Không thể tạo QR thanh toán');
        setQrModalVisible(false);
      }
    } catch (error) {
      console.error('Error generating QR:', error);
      message.error('Lỗi khi tạo QR thanh toán');
      setQrModalVisible(false);
    } finally {
      setQrLoading(false);
    }
  };

  // Duyệt rút tiền từ QR Modal
  const handleApproveFromQR = async () => {
    if (!selectedRecord) return;

    Modal.confirm({
      title: 'Xác nhận duyệt rút tiền',
      content: (
        <div>
          <p>Bạn có chắc chắn đã chuyển khoản và muốn duyệt yêu cầu rút tiền này?</p>
          <p><strong>Khách hàng:</strong> {selectedRecord.user?.name}</p>
          <p><strong>Số tiền:</strong> {selectedRecord.withdrawal?.withdrawal_amount?.toLocaleString()} VNĐ</p>
          <p><strong>Thực nhận:</strong> {qrData?.payment_info?.so_tien?.toLocaleString()} VNĐ</p>
        </div>
      ),
      okText: 'Duyệt',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const response = await withdrawalService.approveWithdrawalByInvoice(
            selectedRecord.id,
            'Đã chuyển khoản qua QR Code'
          );
          
          if (response.success) {
            message.success(response.message || 'Duyệt rút tiền thành công');
            setQrModalVisible(false);
            setQrData(null);
            setSelectedRecord(null);
            fetchWithdrawals();
          } else {
            message.error(response.message || 'Lỗi không xác định');
          }
        } catch (error) {
          console.error('Error approving withdrawal:', error);
          message.error('Lỗi khi duyệt rút tiền');
        }
      }
    });
  };

  // Đóng QR Modal
  const handleCloseQRModal = () => {
    setQrModalVisible(false);
    setQrData(null);
    setSelectedRecord(null);
  };

  // Duyệt rút tiền theo đơn
  const handleApproveSingle = async (record) => {
    Modal.confirm({
      title: 'Duyệt rút tiền',
      content: (
        <div>
          <p>Bạn có chắc chắn muốn duyệt yêu cầu rút tiền này?</p>
          <p><strong>Khách hàng:</strong> {record.user?.name}</p>
          <p><strong>Số tiền:</strong> {record.withdrawal?.withdrawal_amount?.toLocaleString()} VNĐ</p>
        </div>
      ),
      okText: 'Duyệt',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const response = await withdrawalService.approveWithdrawalByInvoice(
            record.id,
            'Duyệt từ admin portal'
          );
          
          if (response.success) {
            message.success(response.message || 'Duyệt rút tiền thành công');
            fetchWithdrawals(); // Reload data
          } else {
            message.error(response.message || 'Lỗi không xác định');
          }
        } catch (error) {
          console.error('Error approving single withdrawal:', error);
          message.error('Lỗi khi duyệt rút tiền');
        }
      }
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id
    },
    {
      title: 'Thông tin khách hàng',
      key: 'user_info',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
            {record.user?.name || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            📱 {record.user?.phone || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ✉️ {record.user?.email || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Thông tin rút tiền',
      key: 'withdrawal_info',
      width: 280,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Trước: <span style={{ color: '#1890ff', fontWeight: 500 }}>
              {record.withdrawal?.amount_before?.toLocaleString() || 0} VNĐ
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Sau: <span style={{ color: '#52c41a', fontWeight: 500 }}>
              {record.withdrawal?.amount_after?.toLocaleString() || 0} VNĐ
            </span>
          </div>
          <div style={{ fontWeight: 500, color: '#e67e22', marginBottom: 4 }}>
            <BankOutlined style={{ marginRight: 4 }} />
            Rút: {record.withdrawal?.withdrawal_amount?.toLocaleString() || 0} VNĐ
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Loại: {record.withdrawal?.invoice_type || 'N/A'}
          </div>
          {record.withdrawal?.note && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
              📝 {record.withdrawal.note}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Thông tin ngân hàng',
      key: 'bank_info',
      width: 200,
      render: (_, record) => (
        <div>
          {record.bank_info ? (
            <>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>
                🏦 {record.bank_info.bank_name || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                STK: {record.bank_info.account_number || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                Chủ TK: {record.bank_info.account_holder || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Mã NH: {record.bank_info.bank_code || 'N/A'}
              </div>
            </>
          ) : (
            <div style={{ color: '#ccc', fontSize: '12px' }}>
              Chưa có thông tin NH
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const status = record.withdrawal?.status;
        let color = 'default';
        let icon = null;
        
        if (status === 'Đợi Duyệt') {
          color = 'processing';
          icon = <CalendarOutlined />;
        } else if (status === 'Đã Duyệt') {
          color = 'success';
          icon = <CheckOutlined />;
        }
        
        return (
          <Tag color={color} icon={icon}>
            {status || 'N/A'}
          </Tag>
        );
      },
      filters: [
        { text: 'Đợi Duyệt', value: 'Đợi Duyệt' },
        { text: 'Đã Duyệt', value: 'Đã Duyệt' }
      ],
      onFilter: (value, record) => record.withdrawal?.status === value
    },
    {
      title: 'Thời gian',
      key: 'timestamps',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            📅 {record.timestamps?.created_at ? 
              dayjs(record.timestamps.created_at).format('DD/MM/YY HH:mm') : 'N/A'}
          </div>
        </div>
      ),
      sorter: (a, b) => {
        const timeA = a.timestamps?.created_at ? new Date(a.timestamps.created_at) : new Date(0);
        const timeB = b.timestamps?.created_at ? new Date(b.timestamps.created_at) : new Date(0);
        return timeA - timeB;
      }
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              Chi tiết
            </Button>
          </Tooltip>
          {record.withdrawal?.status === 'Đợi Duyệt' && (
            <Tooltip title="QR Thanh toán">
              <Button
                type="primary"
                size="small"
                icon={<QrcodeOutlined />}
                onClick={() => handleShowQRPayment(record)}
              >
                QR
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const handleViewDetail = (record) => {
    Modal.info({
      title: 'Chi tiết yêu cầu rút tiền',
      width: 600,
      content: (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>ID:</strong></Col>
            <Col span={16}>{record.id}</Col>
            
            <Col span={8}><strong>Khách hàng:</strong></Col>
            <Col span={16}>{record.user?.name} (#{record.user?.id})</Col>
            
            <Col span={8}><strong>Điện thoại:</strong></Col>
            <Col span={16}>{record.user?.phone}</Col>
            
            <Col span={8}><strong>Email:</strong></Col>
            <Col span={16}>{record.user?.email}</Col>
            
            <Col span={8}><strong>Số tiền trước:</strong></Col>
            <Col span={16}><Text type="default">{record.withdrawal?.amount_before?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Số tiền sau:</strong></Col>
            <Col span={16}><Text type="success">{record.withdrawal?.amount_after?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Số tiền rút:</strong></Col>
            <Col span={16}><Text type="warning" strong>{record.withdrawal?.withdrawal_amount?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Loại hóa đơn:</strong></Col>
            <Col span={16}>{record.withdrawal?.invoice_type}</Col>
            
            <Col span={8}><strong>Trạng thái:</strong></Col>
            <Col span={16}>
              <Tag color={record.withdrawal?.status === 'Đã Duyệt' ? 'success' : 'processing'}>
                {record.withdrawal?.status}
              </Tag>
            </Col>
            
            <Col span={24} style={{ marginTop: 16, marginBottom: 8 }}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>Thông tin ngân hàng:</strong>
            </Col>
            
            {record.bank_info ? (
              <>
                <Col span={8}><strong>Ngân hàng:</strong></Col>
                <Col span={16}>{record.bank_info.bank_name} ({record.bank_info.bank_code})</Col>
                
                <Col span={8}><strong>Số tài khoản:</strong></Col>
                <Col span={16}><Text copyable>{record.bank_info.account_number}</Text></Col>
                
                <Col span={8}><strong>Chủ tài khoản:</strong></Col>
                <Col span={16}>{record.bank_info.account_holder}</Col>
              </>
            ) : (
              <Col span={24}>
                <Text type="secondary">Chưa có thông tin ngân hàng</Text>
              </Col>
            )}
            
            <Col span={24} style={{ marginTop: 16, marginBottom: 8 }}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>Thông tin thời gian:</strong>
            </Col>
            
            <Col span={8}><strong>Thời gian tạo:</strong></Col>
            <Col span={16}>
              {record.timestamps?.created_at ? 
                dayjs(record.timestamps.created_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
            </Col>
            
            {record.withdrawal?.note && (
              <>
                <Col span={8}><strong>Ghi chú:</strong></Col>
                <Col span={16}>{record.withdrawal.note}</Col>
              </>
            )}
          </Row>
        </div>
      )
    });
  };

  const handleSearch = () => {
    fetchWithdrawals();
  };

  const handleReset = () => {
    setDateRange([]);
    setStatusFilter('');
    // Reload data
    setTimeout(() => {
      fetchWithdrawals();
    }, 100);
  };

  const handleExportExcel = () => {
    try {
      const now = dayjs();
      const dateRangeText = dateRange.length === 2 
        ? `Từ ${dateRange[0].format('DD/MM/YYYY')} đến ${dateRange[1].format('DD/MM/YYYY')}`
        : 'Tất cả thời gian';
      
      // Calculate summary statistics
      const totalWithdrawal = data.reduce((sum, item) => sum + (item.withdrawal?.withdrawal_amount || 0), 0);
      const totalActual = Math.round(totalWithdrawal * 0.98);
      const totalFee = totalWithdrawal - totalActual;
      const pendingCount = data.filter(item => item.withdrawal?.status === 'Đợi Duyệt').length;
      const approvedCount = data.filter(item => item.withdrawal?.status === 'Đã Duyệt').length;
      
      // Prepare data for Excel
      const excelData = data.map((item, index) => {
        const withdrawalAmount = item.withdrawal?.withdrawal_amount || 0;
        const actualAmount = Math.round(withdrawalAmount * 0.98);
        const fee = withdrawalAmount - actualAmount;
        
        return {
          'STT': index + 1,
          'Họ và tên': item.user?.name || '',
          'Số điện thoại': item.user?.phone || '',
          'Email': item.user?.email || '',
          'Số dư trước': item.withdrawal?.amount_before || 0,
          'Số dư sau': item.withdrawal?.amount_after || 0,
          'Số tiền rút': withdrawalAmount,
          'Phí dịch vụ': fee,
          'Số tiền thực nhận': actualAmount,
          'Trạng thái': item.withdrawal?.status || '',
          'Ngân hàng': item.bank_info?.bank_name || '',
          'Số tài khoản': item.bank_info?.account_number || '',
          'Chủ tài khoản': item.bank_info?.account_holder || '',
          'Mã ngân hàng': item.bank_info?.bank_code || '',
          'Loại giao dịch': item.withdrawal?.invoice_type || '',
          'Ghi chú': item.withdrawal?.note || '',
          'Ngày tạo': item.timestamps?.created_at ? 
            dayjs(item.timestamps.created_at).format('DD/MM/YYYY') : '',
          'Giờ tạo': item.timestamps?.created_at ? 
            dayjs(item.timestamps.created_at).format('HH:mm:ss') : ''
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      
      // Create header info as separate rows
      const headerRows = [
        ['BÁO CÁO RÚT TIỀN'],
        [''],
        ['Công ty:', 'Linh Dược Sơn'],
        ['Thời gian xuất:', now.format('DD/MM/YYYY HH:mm:ss')],
        ['Khoảng thời gian:', dateRangeText],
        ['Người xuất:', 'Admin'],
        [''],
        ['TỔNG QUAN'],
        ['Tổng số giao dịch:', data.length],
        ['Đang chờ duyệt:', pendingCount],
        ['Đã duyệt:', approvedCount],
        ['Tổng tiền rút:', totalWithdrawal.toLocaleString('vi-VN')],
        ['Tổng phí dịch vụ:', totalFee.toLocaleString('vi-VN')],
        ['Tổng tiền thực nhận:', totalActual.toLocaleString('vi-VN')],
        [''],
        ['CHI TIẾT GIAO DỊCH']
      ];

      // Create worksheet from header
      const ws = XLSX.utils.aoa_to_sheet(headerRows);
      
      // Add data table starting from row 17
      XLSX.utils.sheet_add_json(ws, excelData, { origin: 'A17' });
      
      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // STT
        { wch: 20 },  // Họ và tên
        { wch: 15 },  // Số điện thoại
        { wch: 25 },  // Email
        { wch: 15 },  // Số dư trước
        { wch: 15 },  // Số dư sau
        { wch: 15 },  // Số tiền rút
        { wch: 15 },  // Phí dịch vụ
        { wch: 18 },  // Số tiền thực nhận
        { wch: 12 },  // Trạng thái
        { wch: 20 },  // Ngân hàng
        { wch: 18 },  // Số tài khoản
        { wch: 20 },  // Chủ tài khoản
        { wch: 15 },  // Mã ngân hàng
        { wch: 15 },  // Loại giao dịch
        { wch: 30 },  // Ghi chú
        { wch: 12 },  // Ngày tạo
        { wch: 10 }   // Giờ tạo
      ];
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Báo cáo rút tiền');
      
      // Generate filename
      const filename = `BaoCao_RutTien_${now.format('YYYYMMDD_HHmmss')}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      message.success(`Xuất báo cáo Excel thành công: ${filename}`);
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Lỗi khi xuất báo cáo Excel');
    }
  };

  const pendingCount = data.filter(item => item.withdrawal?.status === 'Đợi Duyệt').length;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            <BankOutlined style={{ marginRight: 8 }} />
            Quản Lý Rút Tiền
          </Title>
          <Space>
            <Button 
              type="default"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={data.length === 0}
            >
              Xuất Excel ({data.length})
            </Button>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchWithdrawals}
              loading={loading}
            >
              Tải lại
            </Button>
          </Space>
        </div>

        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Tổng yêu cầu"
                value={summary.total_records}
                prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Đợi duyệt"
                value={summary.pending_count}
                prefix={<Badge status="processing" />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Đã duyệt"
                value={summary.approved_count}
                prefix={<Badge status="success" />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Tổng tiền chờ duyệt"
                value={summary.pending_amount}
                suffix="VNĐ"
                prefix={<BankOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" title="Bộ lọc" style={{ marginBottom: '16px' }}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Khoảng thời gian:</Text>
              </div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Trạng thái:</Text>
              </div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
                placeholder="Chọn trạng thái"
                allowClear
              >
                <Select.Option value="Đợi Duyệt">Đợi Duyệt</Select.Option>
                <Select.Option value="Đã Duyệt">Đã Duyệt</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Thao tác:</Text>
              </div>
              <Space wrap>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Tìm kiếm
                </Button>
                <Button onClick={handleReset}>
                  Đặt lại
                </Button>
                <Button 
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  disabled={data.length === 0}
                >
                  Xuất Excel
                </Button>
                <Button 
                  type="primary"
                  danger
                  icon={<CheckOutlined />}
                  onClick={() => setApprovalModalVisible(true)}
                  disabled={pendingCount === 0}
                >
                  Duyệt hàng loạt ({pendingCount})
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>



        {/* Table */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            total: data.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          size="middle"
        />
      </Card>

      {/* Approval Modal */}
      <Modal
        title="Duyệt rút tiền hàng loạt"
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          setApprovalNote('');
        }}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => {
            setApprovalModalVisible(false);
            setApprovalNote('');
          }}>
            Hủy
          </Button>,
          <Button 
            key="simulate" 
            type="default"
            onClick={handleApproveWithdrawalsSimulate}
            disabled={approvalLoading}
          >
            Mô phỏng duyệt
          </Button>,
          <Button 
            key="approve" 
            type="primary"
            onClick={handleApproveWithdrawals}
            loading={approvalLoading}
          >
            Duyệt thực tế
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Bạn có chắc chắn muốn duyệt <strong>{pendingCount}</strong> yêu cầu rút tiền đang chờ duyệt?
          </Text>
          {dateRange.length === 2 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Trong khoảng thời gian: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
              </Text>
            </div>
          )}
        </div>
        
        <div>
          <Text strong>Ghi chú khi duyệt:</Text>
          <TextArea
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            placeholder="Nhập ghi chú khi duyệt (tùy chọn)..."
            maxLength={500}
            rows={4}
            style={{ marginTop: 8 }}
          />
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {approvalNote.length}/500 ký tự
            </Text>
          </div>
        </div>
      </Modal>

      {/* QR Payment Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrcodeOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <span>QR Thanh Toán</span>
          </div>
        }
        open={qrModalVisible}
        onCancel={handleCloseQRModal}
        width={500}
        footer={null}
      >
        {qrLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Đang tạo QR thanh toán...</div>
          </div>
        ) : qrData ? (
          <div>
            {/* QR Code */}
            <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: 16 }}>
              <div style={{ 
                display: 'inline-block',
                padding: 16,
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <QRCode
                  value={qrData.qr_string}
                  size={280}
                  errorLevel="H"
                />
              </div>
              <div style={{ marginTop: 12, color: '#666' }}>
                Quét mã QR để thanh toán
              </div>
            </div>

            {/* Nút Hủy và Duyệt */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Space size="middle">
                <Button 
                  size="large"
                  onClick={handleCloseQRModal}
                >
                  Hủy
                </Button>
                <Button 
                  type="primary"
                  size="large"
                  icon={<CheckOutlined />}
                  onClick={handleApproveFromQR}
                >
                  Duyệt
                </Button>
              </Space>
            </div>

            {/* Thông tin khách hàng */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={8}><strong>Khách hàng:</strong></Col>
                <Col span={16}>{selectedRecord?.user?.name}</Col>
                
                <Col span={8}><strong>Số điện thoại:</strong></Col>
                <Col span={16}>{selectedRecord?.user?.phone}</Col>
                
                <Col span={8}><strong>Số tiền rút:</strong></Col>
                <Col span={16}>
                  <Text type="warning" strong>
                    {selectedRecord?.withdrawal?.withdrawal_amount?.toLocaleString()} VNĐ
                  </Text>
                </Col>
                
                <Col span={8}><strong>Thực nhận (98%):</strong></Col>
                <Col span={16}>
                  <Text type="success" strong>
                    {qrData.payment_info?.so_tien?.toLocaleString()} VNĐ
                  </Text>
                </Col>
              </Row>
            </Card>

            {/* Thông tin ngân hàng */}
            <Card size="small" title="Thông tin chuyển khoản" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={8}><strong>Ngân hàng:</strong></Col>
                <Col span={16}>{selectedRecord?.bank_info?.bank_name}</Col>
                
                <Col span={8}><strong>Số tài khoản:</strong></Col>
                <Col span={16}>
                  <Text copyable>{qrData.payment_info?.stk}</Text>
                </Col>
                
                <Col span={8}><strong>Chủ tài khoản:</strong></Col>
                <Col span={16}>{selectedRecord?.bank_info?.account_holder}</Col>
                
                <Col span={8}><strong>Nội dung CK:</strong></Col>
                <Col span={16}>
                  <Text copyable code>{qrData.payment_info?.noi_dung_ck}</Text>
                </Col>
              </Row>
            </Card>

            {/* Hướng dẫn */}
            <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}>
              <div style={{ fontSize: 12, color: '#1890ff' }}>
                <strong>📱 Hướng dẫn:</strong>
                <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>Mở ứng dụng ngân hàng</li>
                  <li>Quét mã QR hoặc nhập thông tin chuyển khoản</li>
                  <li>Kiểm tra thông tin và xác nhận thanh toán</li>
                  <li>Sau khi chuyển khoản thành công, nhấn nút "Duyệt"</li>
                </ol>
              </div>
            </Card>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            Không thể tạo QR thanh toán
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WithdrawalList;